
import CustomSelect, { CustomSelectItem } from "components/custom-ui/CustomSelect";
import { CenterContentContainer, ContentHeaderView, FormContainer, FormRowView, PageContainer, PageContentView } from "components/shared";
import { FormButtonRow } from "components/shared/containers/FormContainer";
import { PrimaryButton, SecondaryButton, UIAmountInput } from "components/ui";
import React from "react";
import { ImageAsset, SVGAsset } from "res";
import CheckPictureWidget, { BackCheckPictureWidget, FrontCheckPictureWidget } from "./components/CheckPictureWidget";
import { getDefault, getWorkflowSteps } from "components/workflows/DepositACheckWorkflow";
import { BCMSCurrency, BCMSDefaultModel, BCMSRecord, useGlobalController, useRoute, useWorkflowStep, BrandingStyle } from "bcms-react";
import "./styles.css";
import Classes, { Account, Deposit } from "model";

import { StateManager, BCMSStateManagerCRUD, useStateManager, useWorkflow } from "bcms-react";

export default function DepositACheckPage(props) {
    const { goStep, nextStep } = useWorkflow() ?? {};

    const globalController = useGlobalController();
    BCMSRecord.globalControllerFor(Classes, globalController);

    const data = { ...getDefaultPageData(), ...props.data ?? {} };
    const ui = { ...getDefaultPageUI(), ...props.ui ?? {} };

    const state = { data, ui };
    const controller = (state, setState) => ({
        data: state.data,
        ui: state.ui,

        onSelectChange: value => {
            BCMSStateManagerCRUD.init(state)
                .update(["data", "deposit", "account"], value === "selectAnAccount" ? undefined : value)
                .submit(setState);
        },
        onAmountBlur: value => {
            BCMSStateManagerCRUD.init(state)
                .update(["data", "deposit", "amount"], value)
                .submit(setState)
        },
        onCheckClick: value => evt => {
            BCMSStateManagerCRUD.init({ ui: props.ui, data: props.data })
                .update(["ui", "selectedCheckView"], value)
                .update(["data", "deposit"], data.deposit)
                .submit(nextStep);
        },

        onDepositClick: evt => {
            const defaultDeposit = BCMSDefaultModel.deposit();
            const mergedRecord = BCMSDefaultModel.mergeRecords(data.deposit, defaultDeposit);

            Deposit.create(mergedRecord)
                .submit();

            const result = BCMSStateManagerCRUD.init({ ui: props.ui, data: props.data })
                .update(["data", "deposit"], Deposit.init(mergedRecord))
                .submit();
            goStep(2, result);
        }
    });

    return (
        <PageContainer className="deposit-a-check-page" state={state} controller={controller}>
            <DepositACheckContent />
        </PageContainer>
    );
}

export const getDefaultPageData = () => ({
    deposit: Deposit.init({})
});
export const getDefaultPageUI = () => ({
    accountDropdown: { isOpen: false },
    checkFrontView: { isSet: false },
    checkBackView: { isSet: false }
});

function DepositACheckContent(props) {
    const { data, ui, onAmountBlur, onDepositClick } = useStateManager();
    const navigate = useRoute();

    const cancelClick = evt => navigate("/accountSummary");
    const viewDepositHistoryClick = evt => navigate("/depositHistory");

    const deposit = data.deposit;
    const amount = deposit.amount.amount ? deposit.amount.format().slice(1) : "";
    const accountID = deposit.account ? deposit.account.id : undefined;
    const isDisabled = deposit.amount.amount !== undefined && accountID !== undefined && ui.checkFrontView.isSet && ui.checkBackView.isSet;

    const windowMabi23Branding = window.mabi23Branding;
    let mabi23Branding = { colors: {}, options: {} };
    let mabi23BrandingOptions = { options: {} };

    if(windowMabi23Branding) {
        mabi23Branding = windowMabi23Branding;
        mabi23BrandingOptions = mabi23Branding.options;
    }

    return (
        <PageContentView>
            <CenterContentContainer>
                <div className="endorsement-instructions-container hide">
                    <div className="flex-container flex-vertical">
                        <p className="text-center endorse-text">Please endorse the back of the check as shown below:</p>
                        <b className="text-center mobile-deposit-text" data-point="Deposit_For_Mobile">For mobile deposit only</b>
                        <i className="text-center your-name-here-text">[Your Name Here]</i>
                    </div>
                </div>
                <ContentHeaderView text="Deposit A Check" dataPoint="Deposit_Check_Title" />

                <FormContainer className="deposit-a-check-form">
                    <FormRowView text="* Deposit To">
                        <DepositAccountSelect dataPoint="Deposit_Account_Select" />
                    </FormRowView>

                    <FormRowView text="Check Amount">
                        <UIAmountInput value={amount} onBlur={onAmountBlur} dataPoint="Deposit_Amount" />
                    </FormRowView>
                    { mabi23BrandingOptions.endorsement_text_mabi23 && 
                    <div className="endorsement-text-container">
                        { mabi23BrandingOptions.endorsement_text_mabi23 }
                    </div>
                    }   
                    <div className="flex-container flex-horizontal align-center space-between">
                        <CheckFrontImage />
                        <CheckBackImage />
                    </div>

                    <FormButtonRow>
                        <SecondaryButton text="Cancel" onClick={cancelClick}></SecondaryButton>
                        <PrimaryButton text="Deposit" className={!isDisabled ? "disabled-button" : ""} onClick={onDepositClick} dataPoint="Deposit_Done"></PrimaryButton>
                    </FormButtonRow>

                    <FormButtonRow className="deposit-history-button-container">
                        <SecondaryButton text="View Deposit History" dataPoint="Deposit_View_History" onClick={viewDepositHistoryClick}></SecondaryButton>
                    </FormButtonRow>
                </FormContainer>
            </CenterContentContainer>
        </PageContentView>
    );
}

function CheckFrontImage(props) {
    const { ui, onCheckClick } = useStateManager();
    const { isSet } = ui.checkFrontView;

    return (
        <>
            {isSet &&
                <CheckPictureWidget text="Front of Check"
                    checkWorkflow={<><FrontCheckPictureWidget dataPoint="Deposit_Front_Check" image={<><ImageAsset id="CheckFront" className="check-image check" /></>}
                        onClick={onCheckClick("front")} /></>} />
            }

            {!isSet &&
                <CheckPictureWidget text="Front of Check"
                    checkWorkflow={<><FrontCheckPictureWidget dataPoint="Deposit_Front_Check" image={<><BrandingStyle style={{ "fill" : "links_abi22" }} element={ <SVGAsset id="CameraIcon" /> } /></>}
                        onClick={onCheckClick("front")} /></>} />
            }
        </>
    );
}

function CheckBackImage(props) {
    const { ui, onCheckClick } = useStateManager();
    const { isSet } = ui.checkBackView;

    return (
        <>
            {isSet &&
                <CheckPictureWidget text="Back of Check"
                    checkWorkflow={<><BackCheckPictureWidget dataPoint="Deposit_Back_Check" image={<><ImageAsset id="CheckBack" className="check-image check" /></>}
                        onClick={onCheckClick("back")} /></>} />
            }

            {!isSet &&
                <CheckPictureWidget text="Back of Check"
                    checkWorkflow={<><BackCheckPictureWidget dataPoint="Deposit_Back_Check" image={<><BrandingStyle style={{ "fill" : "links_abi22" }} element={ <SVGAsset id="CameraIcon" /> } /></>}
                        onClick={onCheckClick("back")} /></>} />
            }
        </>
    );
}

function DepositAccountSelect(props) {
    const { onSelectChange, data, ui } = useStateManager();

    const value = data.deposit.account ? data.deposit.account.id.toString() : "selectAnAccount";
    const { isOpen } = ui.accountDropdown;

    const accountItems = Account.initAll().reduce((result, account) => {
        const description = account.type.toLowerCase() === "loan" && account.availableBalance
            ? ` (Available ${account.availableBalance.format()})`
            : `${account.number} - ${account.nickname}`;

        return [...result, <CustomSelectItem value={account.id.toString()} key={`Account_${account.id}`} dataPoint={`Account_${account.id}`}>{description}</CustomSelectItem>];
    }, [<CustomSelectItem value="selectAnAccount" key={"selectAnAccount"}>-- Select an Account --</CustomSelectItem>]);

    return (
        <CustomSelect value={value} active={isOpen} onChange={onSelectChange} dataPoint={props.dataPoint}>
            {accountItems}
        </CustomSelect>
    );
}